import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axios';
import './AuthForm.css';
import { storeUser } from '../utils/auth';

interface AuthFormProps {
    onLoginSuccess: () => void;
}

const AuthForm: React.FC<AuthFormProps> = ({ onLoginSuccess }) => {
    const [isLoginForm, setIsLoginForm] = useState(true);
    const [isForgotPasswordForm, setIsForgotPasswordForm] = useState(false);
    const [email, setEmail] = useState('');
    const [initials,setInitials] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [username, setUsername] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [message, setMessage] = useState<string>('');
    const [isError, setIsError] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    // useRef to persist the error/feedback message across re-renders
    const messageRef = useRef<string>('');

    // Clear the persisted message when user interacts with the form.
    const clearMessage = () => {
        if (messageRef.current !== '') {
            messageRef.current = '';
            setMessage('');
            setIsError(false);
        }
    };

    useEffect(() => {
        if (messageRef.current) {
            setMessage(messageRef.current);
        }
    }, [isLoginForm, isForgotPasswordForm]); // Ensure message persists when toggling forms

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await axios.post('/api/Auth/register', { email, username, password, initials });
            const successMsg = 'Registration successful! Please check your email to confirm your account.';
            messageRef.current = successMsg;
            setMessage(successMsg);
            setIsError(false);
            setIsLoginForm(true); // Switch to login form after successful registration
            setIsForgotPasswordForm(false);
        } catch (error){
            // Extract error message from response if available
            let errorMsg = 'Registration failed. Please try again.';
            if (axios.isAxiosError(error) && error.response) {
                errorMsg = error.response.data || errorMsg;
            }
            messageRef.current = errorMsg;
            setMessage(errorMsg);
            setIsError(true);
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/Auth/login', { 
                email, 
                password,
                rememberMe 
            });
            storeUser(response.data.user);
            // Clear any persisted error message upon successful login
            messageRef.current = '';
            setMessage('');
            setIsError(false);
            onLoginSuccess();
        } catch (error) {
            let errMsg = 'Login failed. Please try again.';
            if (axios.isAxiosError(error) && error.response) {
                errMsg = error.response.data || errMsg;
            }
            messageRef.current = errMsg;
            setMessage(errMsg);
            setIsError(true);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await axios.post('/api/Auth/forgotPassword', { email });
            const successMsg = 'Password reset instructions have been sent to your email.';
            messageRef.current = successMsg;
            setMessage(successMsg);
            setIsError(false);
            setResetToken(''); // Clear any existing token
        } catch (error) {
            let errorMsg = 'Failed to request password reset. Please try again.';
            if (axios.isAxiosError(error) && error.response) {
                errorMsg = error.response.data || errorMsg;
            }
            messageRef.current = errorMsg;
            setMessage(errorMsg);
            setIsError(true);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setMessage('Passwords do not match');
            setIsError(true);
            return;
        }

        try {
            await axios.post('/api/Auth/resetPassword', { 
                email,
                token: resetToken,
                newPassword: password
            });
            const successMsg = 'Password has been reset successfully. You can now login.';
            messageRef.current = successMsg;
            setMessage(successMsg);
            setIsError(false);
            setIsForgotPasswordForm(false);
            setIsLoginForm(true);
            setPassword('');
            setConfirmPassword('');
            setResetToken('');
        } catch (error) {
            let errorMsg = 'Failed to reset password. The link may have expired.';
            if (axios.isAxiosError(error) && error.response) {
                errorMsg = error.response.data || errorMsg;
            }
            messageRef.current = errorMsg;
            setMessage(errorMsg);
            setIsError(true);
        }
    };

    const renderForgotPasswordForm = () => {
        if (resetToken) {
            // Reset password form with token (user clicked link from email)
            return (
                <div className="auth-form">
                    <h3>Reset Password</h3>
                    <form onSubmit={handleResetPassword} className="auth-form-form">
                        <input 
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="Email" 
                            value={email}
                            onChange={(e) => { clearMessage(); setEmail(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <input 
                            type="password" 
                            name="new-password"
                            autoComplete="new-password"
                            placeholder="New Password"
                            value={password} 
                            onChange={(e) => { clearMessage(); setPassword(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <input 
                            type="password" 
                            name="confirm-password"
                            autoComplete="new-password"
                            placeholder="Confirm Password"
                            value={confirmPassword} 
                            onChange={(e) => { clearMessage(); setConfirmPassword(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <button type="submit" className="auth-form-button">Reset Password</button>
                        <button 
                            type="button" 
                            className="auth-form-link"
                            onClick={() => {
                                setIsForgotPasswordForm(false); 
                                setIsLoginForm(true);
                                clearMessage();
                            }}
                        >
                            Back to Login
                        </button>
                    </form>
                </div>
            );
        } else {
            // Initial forgot password form (email only)
            return (
                <div className="auth-form">
                    <h3>Forgot Password</h3>
                    <form onSubmit={handleForgotPassword} className="auth-form-form">
                        <input 
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="Email" 
                            value={email}
                            onChange={(e) => { clearMessage(); setEmail(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <button type="submit" className="auth-form-button">Send Reset Link</button>
                        <button 
                            type="button" 
                            className="auth-form-link"
                            onClick={() => {
                                setIsForgotPasswordForm(false); 
                                setIsLoginForm(true);
                                clearMessage();
                            }}
                        >
                            Back to Login
                        </button>
                    </form>
                </div>
            );
        }
    };

    // Check URL for reset token when component mounts
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const emailParam = params.get('email');
        
        if (token && emailParam) {
            // Syncing external state (URL params) into React on mount - valid pattern
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setResetToken(token);
            setEmail(emailParam);
            setIsForgotPasswordForm(true);
            setIsLoginForm(false);
        }
    }, []);

    return (
        <div className="auth-container" onClick={clearMessage}>
            <div className="auth-toggle">
                <button 
                    className={isLoginForm && !isForgotPasswordForm ? 'active' : ''} 
                    onClick={() => {
                        setIsLoginForm(true);
                        setIsForgotPasswordForm(false);
                    }}
                >
                    Login
                </button>
                <button 
                    className={!isLoginForm && !isForgotPasswordForm ? 'active' : ''} 
                    onClick={() => {
                        setIsLoginForm(false);
                        setIsForgotPasswordForm(false);
                    }}
                >
                    Register
                </button>
            </div>

            {message && (
                <div className={`message ${isError ? 'error' : 'success'}`}>
                    {message}
                </div>
            )}

            {isLoginForm && !isForgotPasswordForm ? (
                <div className="auth-form">
                    <h3>Login</h3>
                    <form onSubmit={handleLogin} className="auth-form-form">
                        <input 
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="Email" 
                            value={email}
                            onChange={(e) => { clearMessage(); setEmail(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <input 
                            type="password" 
                            name="password"
                            autoComplete="current-password"
                            placeholder="Password"
                            value={password} 
                            onChange={(e) => { clearMessage(); setPassword(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <label className="remember-me auth-form-label">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => { clearMessage(); setRememberMe(e.target.checked); }}
                            />
                            Remember me
                        </label>                    
                        <button type="submit" className="auth-form-button">Login</button>
                        <div style={{ textAlign: 'left', marginTop: '5px' }}>
                            <button 
                                type="button" 
                                className="forgot-password-link"
                                onClick={() => {
                                    setIsForgotPasswordForm(true);
                                    setIsLoginForm(false);
                                    clearMessage();
                                }}
                            >
                                Forgot Password?
                            </button>
                        </div>
                    </form>
                </div>
            ) : !isLoginForm && !isForgotPasswordForm ? (
                <div className="auth-form">
                    <h3>Register</h3>
                    <form onSubmit={handleRegister} className="auth-form-form">
                        <input 
                            type="text" 
                            name="username"
                            autoComplete="username"
                            placeholder="Username"
                            value={username} 
                            onChange={(e) => { clearMessage(); setUsername(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <input 
                            type="text" 
                            name="initials"
                            autoComplete="initials"
                            placeholder="initials"
                            value={initials} 
                            onChange={(e) => { clearMessage(); setInitials(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <input 
                            type="email"
                            name="email"
                            autoComplete="email"
                            placeholder="Email"
                            value={email} 
                            onChange={(e) => { clearMessage(); setEmail(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <input 
                            type="password" 
                            name="password"
                            autoComplete="new-password"
                            placeholder="Password"
                            value={password} 
                            onChange={(e) => { clearMessage(); setPassword(e.target.value); }}
                            className="auth-form-input"
                            required
                        />
                        <button type="submit" className="auth-form-button">Register</button>
                    </form>
                </div>
            ) : (
                renderForgotPasswordForm()
            )}
        </div>
    );
};

export default AuthForm;